// node modules
import Vue, { VNode } from 'vue'
import VueI18n from 'vue-i18n'
import VTooltip from 'v-tooltip'
import applyVTooltipLeakPatch from '@/plugins/v-tooltip-leak-patch'
import Toasted from 'vue-toasted'
import PortalVue from 'portal-vue'
import VueCookie from 'vue-cookie'
import Clipboard from 'v-clipboard'
import vClickOutside from 'v-click-outside'
import { VueMasonryPlugin } from 'vue-masonry'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import VueResource, { HttpOptions, HttpResponse } from 'vue-resource'
import ToggleButton from 'vue-js-toggle-button'

// bootstrap components, ONLY import what is needed to reduce size
import { BButton } from 'bootstrap-vue/esm/components/button'
import { BButtonGroup } from 'bootstrap-vue/esm/components/button-group'
import { BFormInput } from 'bootstrap-vue/esm/components/form-input'
import { BFormFile } from 'bootstrap-vue/esm/components/form-file'
import { BFormCheckbox } from 'bootstrap-vue/esm/components/form-checkbox'
import { BModal } from 'bootstrap-vue/esm/components/modal'
import {
  BDropdown,
  BDropdownDivider,
  BDropdownItem,
  BDropdownForm,
} from 'bootstrap-vue/esm/components/dropdown'
import { CollapsePlugin } from 'bootstrap-vue/esm'
import '@/utils/system/SystemConnector'
import { captureDebugParamIfPresent } from '@/services/onboardingIkeaChecklist'

// FIXME: properly type storybook
// @ts-ignore
import AhaPresenter from '@ahaslides-product/stpancras-storybook-app/src/AhaPresenter'
// import mixpanelUtils from './utils/mixpanel.utils'
import load3rdPartyLibs from '@/services/loaderPlugin'
import i18nPlugin from '@/i18nPlugin'
import i18n from '@/i18n'
import i18nPresenter from '@/i18nPresenter'
import router from '@/router'
import { ROUTE_NAMES } from '@/constant/routes'
import store from '@/vuex/store'
import App from '@/App.vue'
import Config from '@/config'
import socket from '@/socket'
import util from '@/util'

import mqttSubscribers from '@/mqtt/subscribers'

// 3rd parties
import sentry from '@/services/sentry'
import { ensureUnleashSession } from '@/services/unleashSession'
import { shouldSendUnleashSessionHeader } from '@/services/unleashSession.requestScope'
import { shouldSendApiCredentials } from '@/utils/apiCredentials.utils'
import unleashService from '@/services/unleash'

// plugins
import variblesPlugin from '@/plugins/varibles'
import functionsPlugin from '@/plugins/functions'

// photoswipe
import 'photoswipe/dist/photoswipe.css'
import 'photoswipe/dist/default-skin/default-skin.css'

// filters
import '@/filters'

// Import Amin Template
import '@/assets/scss-v2/styles.scss'
import '@/assets/scss/animate2.css'
import 'cropperjs/dist/cropper.css'
import '@fontsource/nunito'
import '@fontsource/plus-jakarta-sans'

import '@fontsource/nunito/600.css'
import './assets/tailwind.css'
import './assets/moveable-custom.css'
import * as iconsMap from './fontAwesome'

// Custom directives
import vHoverIntent from '@/directives/hoverIntent'
import EmitActionDirective from '@/directives/emit-action.directive'
import ClickDirective from '@/directives/click.directive'
import LazyLoadDirective from '@/directives/lazyload'
import PreventWheelPropagation from '@/directives/prevent-wheel-propagation.directive'
import { WebVitalsRecord } from './utils/webVitals'

const { v4 } = require('uuid')

sentry.init()

// Resolve the shared Unleash session anchor once at bootstrap, before any
// general-api call or UnleashClient.start(). Read-then-mint: a value set by the
// pricing page (same root domain) is preserved, so visit order never flips the
// variant. Feeds both the client-side flags (setSessionId) and the
// x-unleash-session request header below.
const unleashSessionId = ensureUnleashSession()
unleashService.setSessionId(unleashSessionId)

// The PowerPoint and Google Slide add-ins run inside cross-origin webviews where
// the x-unleash-session request header triggers a CORS preflight that their
// origins reject. Detect those add-in contexts once so the interceptor can skip
// the header in them; client-side flags still resolve via the session id above.
const isSlideAddinContext = Config.isEnableAddinPowerpoint || Config.integrationPlatform === 'GoogleSlide'

// Capture ?onboardingIkeaChecklistDebug=1 before any router redirects strip it.
// Must run before the Vue app boots so that sessionStorage is set for isChecklistEnabled().
captureDebugParamIfPresent()

// mixpanelUtils.init()
// Because this service is no longer dependent on the Unleash Feature Flag, so I move it here
// And Mixpanel and any other script should be loaded sooner to be able to track the event
load3rdPartyLibs()
mqttSubscribers.init()

Vue.use(Clipboard)
Vue.use(VueCookie)
Vue.use(VueResource)
Vue.use(VTooltip, {
  defaultOffset: 8,
  defaultTrigger: 'hover click',
})
// Clear v-tooltip's dangling 5s teardown timers so detached DOM is GC'd promptly.
applyVTooltipLeakPatch()
Vue.use(ToggleButton)
Vue.use(VueI18n)
Vue.use(i18nPlugin, { i18nPresenter })
Vue.use(AhaPresenter);
(window as any).AHA_STORYBOOK_VERSION = AhaPresenter.version

Vue.use(vClickOutside)
Vue.use(VueMasonryPlugin)
Vue.use(Toasted, { duration: 5000, singleton: true })
Vue.use(PortalVue)
Vue.component('FontAwesomeIcon', FontAwesomeIcon)
library.add(iconsMap)

// bootstrap components, ONLY import what is needed to reduce size
Vue.component('BButton', BButton)
Vue.component('BButtonGroup', BButtonGroup)
Vue.component('BFormInput', BFormInput)
Vue.component('BFormFile', BFormFile)
Vue.component('BFormCheckbox', BFormCheckbox)
Vue.component('BModal', BModal)
Vue.component('BDropdown', BDropdown)
Vue.component('BDropdownDivider', BDropdownDivider)
Vue.component('BDropdownItem', BDropdownItem)
Vue.component('BDropdownForm', BDropdownForm)

Vue.use(CollapsePlugin)
// use custom plugins
Vue.use(variblesPlugin)
Vue.use(functionsPlugin)

// use custom directives
Vue.directive('hover-intent', vHoverIntent)
Vue.directive('lazy-load', LazyLoadDirective)
Vue.directive('aha-emit-action', EmitActionDirective)
Vue.directive('aha-click', ClickDirective)
Vue.directive('prevent-wheel-propagation', PreventWheelPropagation)

Vue.config.productionTip = false
if (Config.env === 'staging' || Config.env === 'develop') { // enable devtool for staging/ dev
  Vue.config.devtools = true
}

if (Config.env === 'production' && localStorage.getItem('enable_vue_devtools') === 'true') {
  Vue.config.devtools = true
}

// constant value for interceptors
const applicationJson = 'application/json'

// If catch 401 in api other than in login page, redirect to login page
// These services re-verify the token with a server-to-server call, and any failure of that hop —
// 5xx, timeout, bad client credentials — comes back as a 401. Theirs is not a verdict on the
// session, so treating it as one would log the user out because an import failed.
const SESSION_AGNOSTIC_PATHS = ['/api/docsys', '/ai/langgraph', '/api/ai-v2']

const AhaInterceptor = (request: HttpOptions, next: any): any => {
  try {
    const ahaSequenceNumber = store.state.ahaSequenceNumber + 1
    store.commit('setAhaSequenceNumber', ahaSequenceNumber)
    if (
      unleashSessionId
      && !isSlideAddinContext
      && shouldSendUnleashSessionHeader(request.url, {
        apiBaseUrl: Config.baseUrl,
        appHref: window.location.href,
      })
    ) {
      request.headers.set('x-unleash-session', unleashSessionId)
    }
    request.credentials = shouldSendApiCredentials(request.url, Config.baseUrl, window.location.href)
    if (request.method !== 'GET') {
      if (request.body === undefined || !request.body) {
        request.body = {}
      }
      request.body.ahaSequenceNumber = ahaSequenceNumber
      request.body.ahaSessionId = socket.getSocketId()
      request.body.clientTimestamp = util.getCurTimeStamp()
      request.body.traceId = v4()
      request.body.presenterOffsetTime = store.state.offsetTime
    }
  } catch (e) {
    sentry.captureException(`Interceptor error: ${JSON.stringify(e)}`)
  }
  const responseInterceptor = (response: HttpResponse): void => {
    if (!request.url) return

    if (request.url.includes('/api/auth/presenter')) return
    // if (response.status == 403) {
    //   if (request.url.includes('/api/slide/')
    //     || request.url.includes('/api/presentation/update/')
    //     || request.url.includes('/api/presentation/update-next-slide')
    //     || request.url.includes('/api/slide-option/')
    //   ) {
    //     window.location.reload()
    //     return
    //   }
    // }
    if (response.status !== 401) return
    if (SESSION_AGNOSTIC_PATHS.some((path) => String(request.url).includes(path))) return
    if (router.currentRoute.name === ROUTE_NAMES.PRESENTATION_DETAIL_PUBLIC) return

    store.commit('setIsTokenValid', false)
  }

  next(responseInterceptor)
}

Vue.http.interceptors.push(AhaInterceptor)

// eslint-disable-next-line import/prefer-default-export
export const app = new Vue({
  i18n,
  router,
  store,
  render: (h): VNode => h(App),
}).$mount('#app')

try {
  const webVitalRecorder = new WebVitalsRecord()
  webVitalRecorder.recordPerfEvents()
} catch (err) {
  sentry.captureException(err)
}
